/**
 * The three weights the design system has, and nothing else.
 *
 * `primary` is the one thing a screen wants you to do; `secondary` is the
 * alternative next to it; `ghost` is the one you should barely notice. See the
 * system checklist in `~/.claude/plans/grile-practice-design-system.md`.
 *
 * They live in their own module because `src/dialogs` needs the same classes
 * on plain `<button>` elements: the dialogs render inside the modal manager,
 * which the unit suite mounts without a router, and `<Button>` calls
 * `useNavigate` for its premium-lock branch. Two hand-copied class strings is
 * exactly the drift the redesign audit keeps finding, so there is one copy and
 * both sides import it.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'unstyled'

export const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'h-[38px] rounded-md px-[18px] bg-primary text-primary-background text-[13.5px] font-semibold',
  secondary:
    'h-[38px] rounded-md px-[16px] border border-[rgba(var(--over-primary-background),0.16)] bg-transparent text-text-secondary text-[13px] font-medium hover:border-[rgba(var(--over-primary-background),0.4)] hover:text-over-primary-background',
  ghost:
    'h-[38px] rounded-md px-[13px] text-text-secondary text-[13px] font-medium hover:bg-[rgba(var(--over-primary-background),0.06)] hover:text-over-primary-background',
  // What every call site got before variants existed: the old base, with the
  // look supplied entirely by `className`. Still the default, so migrating is
  // one call site at a time rather than one big diff.
  unstyled: 'p-2.5 rounded opacity-90 text-white text-sm font-bold',
}

/** Shared by every weight: the box, and the focus treatment the theme sets. */
export const BUTTON_BASE =
  'flex items-center justify-center whitespace-nowrap focus:outline-hidden'
